const MAX_PATTERN_LENGTH = 512
const PLUS_OR_PERCENT_20_GROUP = '(?:\\+|%20)'

type PatternToken =
    | { type: 'literal'; value: string }
    | { type: 'wildcard' }
    | { type: 'alternatives'; values: string[] }
    | { type: 'anchorStart' }
    | { type: 'anchorEnd' }

type MatchOptions = {
    anchored?: boolean
}

function isSafeDynamicPattern(pattern: string): boolean {
    if (!pattern || pattern.length > MAX_PATTERN_LENGTH) {
        return false
    }

    return !/\\[1-9]|\(\?(?!:)|\{(?:\d{4,}|\d+,\d{4,})\}/.test(pattern)
}

function pushLiteral(tokens: PatternToken[], buffer: string): void {
    if (!buffer) {
        return
    }

    const previous = tokens[tokens.length - 1]
    if (previous?.type === 'literal') {
        previous.value += buffer
        return
    }

    tokens.push({ type: 'literal', value: buffer })
}

function tokenizeSafeDynamicPattern(pattern: string): PatternToken[] | null {
    if (!isSafeDynamicPattern(pattern)) {
        return null
    }

    const tokens: PatternToken[] = []
    let buffer = ''

    for (let i = 0; i < pattern.length; i++) {
        if (pattern.startsWith(PLUS_OR_PERCENT_20_GROUP, i)) {
            pushLiteral(tokens, buffer)
            buffer = ''
            tokens.push({ type: 'alternatives', values: ['+', '%20'] })
            i += PLUS_OR_PERCENT_20_GROUP.length - 1
            continue
        }

        const current = pattern[i]
        if (current === '^' || current === '$') {
            pushLiteral(tokens, buffer)
            buffer = ''
            tokens.push({ type: current === '^' ? 'anchorStart' : 'anchorEnd' })
            continue
        }

        if (current === '\\') {
            const next = pattern[i + 1]
            if (!next) {
                return null
            }

            buffer += next
            i += 1
            continue
        }

        if (current === '.' && pattern[i + 1] === '*') {
            pushLiteral(tokens, buffer)
            buffer = ''
            tokens.push({ type: 'wildcard' })
            i += 1
            continue
        }

        if (current === '*') {
            pushLiteral(tokens, buffer)
            buffer = ''
            tokens.push({ type: 'wildcard' })
            continue
        }

        if (current === '(') {
            const end = pattern.indexOf(')', i + 1)
            if (end === -1) {
                return null
            }

            const group = pattern.slice(i + 1, end)
            const body = group.startsWith('?:') ? group.slice(2) : group
            if (body.includes('|')) {
                pushLiteral(tokens, buffer)
                buffer = ''
                tokens.push({
                    type: 'alternatives',
                    values: body.split('|').map((option) => option.replace(/\\(.)/g, '$1')),
                })
                i = end
                continue
            }

            buffer += body.replace(/\\(.)/g, '$1')
            i = end
            continue
        }

        buffer += current
    }

    pushLiteral(tokens, buffer)
    return tokens
}

/**
 * Whether `tokens` can match `value`, filled in bottom-up.
 *
 * `reachable[t][v]` answers "can tokens from index `t` match the value from index `v`". Every
 * entry is computed once from entries already known, which keeps the work proportional to
 * tokens x value length. The obvious recursive form instead re-scans the remaining value at every
 * wildcard, making the cost grow with the square of the value's length - and these patterns are
 * matched against visitor URLs on every page change, so that cost lands on the publisher's main
 * thread. A table also avoids recursing once per character, which would overflow the stack on a
 * long URL.
 */
function buildReachabilityTable(value: string, tokens: PatternToken[], anchored: boolean): boolean[][] {
    const valueLength = value.length
    const reachable: boolean[][] = Array.from({ length: tokens.length + 1 }, () =>
        new Array<boolean>(valueLength + 1).fill(false),
    )

    // With every token consumed, the match holds if we are allowed to stop here.
    for (let valueIndex = 0; valueIndex <= valueLength; valueIndex++) {
        reachable[tokens.length][valueIndex] = !anchored || valueIndex === valueLength
    }

    for (let tokenIndex = tokens.length - 1; tokenIndex >= 0; tokenIndex--) {
        const token = tokens[tokenIndex]

        // Descending, so a wildcard can read the entry one character to its right in this row.
        for (let valueIndex = valueLength; valueIndex >= 0; valueIndex--) {
            let result = false

            if (token.type === 'anchorStart') {
                result = valueIndex === 0 && reachable[tokenIndex + 1][valueIndex]
            } else if (token.type === 'anchorEnd') {
                result = valueIndex === valueLength && reachable[tokenIndex + 1][valueIndex]
            } else if (token.type === 'wildcard') {
                result =
                    reachable[tokenIndex + 1][valueIndex] ||
                    (valueIndex < valueLength && reachable[tokenIndex][valueIndex + 1])
            } else if (token.type === 'literal') {
                result =
                    value.startsWith(token.value, valueIndex) &&
                    reachable[tokenIndex + 1][valueIndex + token.value.length]
            } else {
                result = token.values.some(
                    (option) =>
                        value.startsWith(option, valueIndex) &&
                        reachable[tokenIndex + 1][valueIndex + option.length],
                )
            }

            reachable[tokenIndex][valueIndex] = result
        }
    }

    return reachable
}

export function testSafeCaseInsensitivePattern(value: string, pattern: string, options: MatchOptions = {}): boolean {
    const tokens = tokenizeSafeDynamicPattern(pattern)
    if (!tokens) {
        return false
    }

    const normalizedValue = value.toLowerCase()
    for (const token of tokens) {
        if (token.type === 'literal') {
            token.value = token.value.toLowerCase()
        } else if (token.type === 'alternatives') {
            token.values = token.values.map((option) => option.toLowerCase())
        }
    }

    const anchored = options.anchored === true
    const reachable = buildReachabilityTable(normalizedValue, tokens, anchored)

    if (anchored) {
        return reachable[0][0]
    }

    // Unanchored: the pattern may begin at any offset in the value.
    return reachable[0].some(Boolean)
}
